/**
 * Автоодобрение — обдуманное исключение из FR-4.1 для брендов, которые его разрешили
 * (решение Ильи 2026-10-25, docs/ai-lab.md). Включённое, оно одобряет каждую НОВУЮ версию
 * писателя бренда, ставшую `pending_review`, в ближайший свободный слот без человека; дальше
 * пост идёт обычным путём публикации.
 *
 * Три замка, каждый держится сам и обязан пропустить бренд:
 *   1. бэкенд — блок конфига `auto_approval.allowed` (`AutoApprovalConfigSchema`), строка
 *      выключателя `system_state` `auto_approval:<бренд>` во включённом положении и триггер
 *      статуса, пропускающий не-человеческое `pending_review → scheduled` только при ней;
 *   2. демон подписи — свой root-owned allowlist брендов;
 *   3. публикатор — свой root-owned allowlist брендов.
 * Ключ автоодобрения подписан `approver: AUTO_APPROVER` — по нему замки 2 и 3 его и узнают.
 *
 * Здесь — провод между бэкендом и интерфейсом: состояние выключателя, тело переключения и
 * след автоодобрения на версии.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/**
 * `details.reason` отказа 409: конфиг бренда автоодобрения не разрешает (замок 1). Включить
 * выключатель такого бренда нельзя, и интерфейс его не показывает.
 */
export const AUTO_APPROVAL_NOT_ALLOWED_REASON = 'auto_approval_not_allowed';

/**
 * Исход работы (`publication_job.outcome_code`) и причина отзыва ключа
 * (`approval_key.revoked_reason`), когда выключение автоодобрения вернуло пост на согласование.
 * Тем же литералом его читает триггер статуса (миграция 0026, ограничение перехода 31): менять
 * слово — значит менять миграцию.
 */
export const AUTO_APPROVAL_OFF_OUTCOME = 'auto_approval_off';

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/auto-approval?brand=  ·  POST /api/v1/auto-approval

/** Положение выключателя. Нет строки или она нечитаема — `off`: исключение не включается само. */
export const AUTO_APPROVAL_STATES = ['off', 'on'] as const;
export const AutoApprovalStateSchema = z.enum(AUTO_APPROVAL_STATES);
export type AutoApprovalState = z.output<typeof AutoApprovalStateSchema>;

/** Кто переключил. Только админ: переключение — `ADMIN_ROUTE`, как режим публикаций. */
export const AutoApprovalActorSchema = z.strictObject({
  id: Uuid,
  login: z.string().min(1),
});

const autoApprovalFields = {
  brand: z.string().regex(BRAND_RE),
  /** Разрешает ли конфиг бренда автоодобрение (замок 1). `false` — выключателя нет. */
  allowed: z.boolean(),
  state: AutoApprovalStateSchema,
  /** С какого момента действует положение; `null` — выключатель никто не трогал. */
  since: IsoUtcMs.nullable(),
  /**
   * Кто поставил положение. `null` — не трогали, ЛИБО выключил сам бэкенд на старте (строка
   * включена, а конфиг бренда больше не разрешает — аудит `auto_approval.forced_off`).
   */
  by: AutoApprovalActorSchema.nullable(),
};

/**
 * Включённым бывает только разрешённый бренд: бэкенд выключает на старте строку бренда, чей
 * конфиг автоодобрение не разрешает. Ответ «включено, но нельзя» — ответ сломанного бэкенда, и
 * показывать его выключателем значило бы врать в самом дорогом месте экрана.
 */
function onlyAllowedIsOn(
  ctx: z.core.ParsePayload<{ allowed: boolean; state: AutoApprovalState }>,
): void {
  if (ctx.value.state === 'on' && !ctx.value.allowed) {
    ctx.issues.push({
      code: 'custom',
      input: ctx.value,
      path: ['state'],
      message: 'auto_approval_on_but_not_allowed',
    });
  }
}

export const AutoApprovalSchema = z.strictObject(autoApprovalFields).check(onlyAllowedIsOn);
export type AutoApproval = z.output<typeof AutoApprovalSchema>;

/**
 * Тело переключения. `expected_state` — положение, которое видел админ (CAS): переключение
 * из соседней вкладки или другим админом между показом и нажатием даёт 409 `state_conflict`,
 * а не молчаливое «включил то, что уже выключили».
 */
export const SetAutoApprovalBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  state: AutoApprovalStateSchema,
  expected_state: AutoApprovalStateSchema,
});
export type SetAutoApprovalBody = z.output<typeof SetAutoApprovalBodySchema>;

/**
 * Ответ переключения — состояние после него плюс то, что сделало выключение. Оба числа есть
 * ВСЕГДА (у включения — нули): одна форма на оба направления, без ветки «поля нет».
 *
 *   • `returned_to_review` — автоодобренные посты, чья работа ещё не была выдана публикатору:
 *     работа отменена, ключ отозван, слот свободен, версия вернулась на согласование (переход 31);
 *   • `left_in_flight` — автоодобренные посты, уже выданные публикатору или отправляющиеся:
 *     их не трогали, исход решит публикатор. Человек обязан видеть это число — выключатель не
 *     остановил эти посты.
 */
export const SetAutoApprovalResponseSchema = z
  .strictObject({
    ...autoApprovalFields,
    returned_to_review: z.int().nonnegative(),
    left_in_flight: z.int().nonnegative(),
  })
  .check(onlyAllowedIsOn)
  .check((ctx) => {
    const v = ctx.value;
    if (v.state === 'on' && (v.returned_to_review !== 0 || v.left_in_flight !== 0)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['returned_to_review'],
        message: 'counts_only_on_off',
      });
    }
  });
export type SetAutoApprovalResponse = z.output<typeof SetAutoApprovalResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// След автоодобрения на версии: бэклог, шапка черновика, модель рендера

/**
 * Что автоодобрение сделало с версией (строка `auto_approval_issue`, одна на версию):
 *   • `issued` — одобрило: ключ выпущен, версия в слоте;
 *   • `skipped` — посмотрело и НЕ одобрило по определённой причине (проверки, гейт картинки,
 *     нет канала, отказ демона подписи, нет свободного слота в материализованной сетке);
 *     повторять оно не будет, решает человек.
 * Версии, которых автоодобрение не касалось, поля не несут вовсе.
 */
export const AUTO_APPROVAL_OUTCOMES = ['issued', 'skipped'] as const;
export type AutoApprovalOutcome = (typeof AUTO_APPROVAL_OUTCOMES)[number];

/**
 * Строгой связи `reason` ↔ `state` здесь нет намеренно: схема входит в модель рендера, а провал
 * её разбора выключает все кнопки решения (09 §9.1) — пропущенная причина не стоит экрана.
 */
export const VersionAutoApprovalSchema = z.strictObject({
  state: z.enum(AUTO_APPROVAL_OUTCOMES),
  /** Причина пропуска словами бэкенда; у `issued` — `null`. */
  reason: z.string().nullable(),
  at: IsoUtcMs,
});
export type VersionAutoApproval = z.output<typeof VersionAutoApprovalSchema>;
